import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth/auth.service';
@Injectable()
export class IsProfileGuard implements CanActivate {

    // ══════════════════════════════════════════════════════════════════
    // MAPA URL → MÓDULO/SUBMÓDULO RBAC
    // Basado en systemModules de roles-permisos.component.ts (matriz de permisos)
    // Los IDs de módulo y submódulo deben coincidir EXACTAMENTE con los de la matriz.
    // Nota: las sections de contactos usan moduleId propio (cupo, planes, gestion_cobro, pagos, compras_financiadas)
    // IMPORTANTE: rutas más específicas DEBEN ir ANTES de las genéricas (url.includes matching)
    // ══════════════════════════════════════════════════════════════════
    private static readonly URL_MODULE_MAP: { pattern: string; modules: string[]; submodule?: string }[] = [

        // ── Contactos (módulo: contactos) ──
        // contactos.busqueda — Búsqueda por Cédula
        { pattern: '/console/search-client', modules: ['contactos'], submodule: 'busqueda' },
        // credito.estatus — Estatus de Aprobación (ANTES de /console/constacts para evitar conflicto de substring)
        { pattern: '/console/constacts-status', modules: ['credito'], submodule: 'estatus' },
        // /console/create-constacts es la pagina contenedora con multiples secciones (busqueda + lista)
        // Solo requiere acceso al modulo contactos; cada seccion se oculta individualmente
        { pattern: '/console/create-constacts', modules: ['contactos'] },
        // contactos.lista_contactos — Lista de Contactos (pagina dedicada)
        { pattern: '/console/constacts', modules: ['contactos'], submodule: 'lista_contactos' },
        // contactos.onboarding — Onboarding
        { pattern: '/console/person/onboarding/', modules: ['contactos'], submodule: 'onboarding' },
        // contactos.perfil — Perfil del Contacto (rutas específicas ANTES del genérico /console/person/)
        { pattern: '/console/person/profile/', modules: ['contactos'], submodule: 'perfil' },
        { pattern: '/console/person/financial-payments/', modules: ['contactos'], submodule: 'perfil' },
        // cobranza.distribucion_pagos — Distribución de Pagos (página por persona) — ANTES del genérico /console/person/
        { pattern: '/console/person/distri/', modules: ['cobranza'], submodule: 'distribucion_pagos' },

        // ── Contactos > Sections (moduleId propio) ──
        // cupo.consultar_cupo — Consultar Cupo
        { pattern: '/console/consult-quota', modules: ['cupo'], submodule: 'consultar_cupo' },
        // gestion_cobro.consultar — Gestión de Cobro
        { pattern: '/console/person/client-installments-view/', modules: ['gestion_cobro'], submodule: 'consultar' },
        // pagos.pagos_registrados / pagos.nuevo_pago — Pagos del contacto
        { pattern: '/console/payments/person-fees/', modules: ['pagos'], submodule: 'pagos_registrados' },
        { pattern: '/console/payments/person/', modules: ['pagos'], submodule: 'pagos_registrados' },
        { pattern: '/console/payment/', modules: ['pagos'], submodule: 'pagos_registrados' },
        { pattern: '/console/post/person/', modules: ['pagos'], submodule: 'nuevo_pago' },
        { pattern: '/console/post', modules: ['pagos'], submodule: 'nuevo_pago' },
        // planes.consultar_planes — Planes del contacto
        { pattern: '/console/person/plans/', modules: ['planes'], submodule: 'consultar_planes' },
        // compras_financiadas — Facturas/Compras
        { pattern: '/console/invoice-detail/', modules: ['compras_financiadas'], submodule: 'compras' },
        // Ruta genérica /console/person/ (perfil)
        { pattern: '/console/person/', modules: ['contactos'], submodule: 'perfil' },

        // ── Ventas (módulo: ventas) ──
        // ventas.facturas — Compras Financiadas
        { pattern: '/console/ventas', modules: ['ventas'], submodule: 'facturas' },
        { pattern: '/console/invoice-dashboard', modules: ['ventas'], submodule: 'facturas' },

        // ── Finanzas (módulo: finanzas) ──
        // finanzas.verificacion_pagos — Verificación de Pagos
        { pattern: '/console/finanzas2', modules: ['finanzas'], submodule: 'verificacion_pagos' },
        { pattern: '/console/finacial-dashboard', modules: ['finanzas'], submodule: 'verificacion_pagos' },
        { pattern: '/console/financial-payments', modules: ['finanzas'], submodule: 'verificacion_pagos' },
        { pattern: '/console/payments', modules: ['finanzas'], submodule: 'verificacion_pagos' },
        { pattern: '/console/cash-register', modules: ['finanzas'], submodule: 'verificacion_pagos' },
        { pattern: '/console/ganancias', modules: ['finanzas'], submodule: 'verificacion_pagos' },

        // ── Cobranza (módulo: cobranza) ──
        // cobranza.afiliaciones — Afiliaciones Bancarias (ANTES de dashboard-direct-debit genérico)
        { pattern: '/console/dashboard-direct-debit-affiliation', modules: ['cobranza'], submodule: 'afiliaciones' },
        { pattern: '/console/direct-debit-afiliations-history', modules: ['cobranza'], submodule: 'afiliaciones' },
        // cobranza.domiciliaciones — Domiciliaciones Bancarias
        { pattern: '/console/dashboard-direct-debit-alliances', modules: ['cobranza'], submodule: 'domiciliaciones' },
        { pattern: '/console/dashboard-direct-debit', modules: ['cobranza'], submodule: 'domiciliaciones' },
        { pattern: '/console/direct-debit-alliances-history', modules: ['cobranza'], submodule: 'domiciliaciones' },
        { pattern: '/console/direct-debit-history', modules: ['cobranza'], submodule: 'domiciliaciones' },
        { pattern: '/console/direct-debit', modules: ['cobranza'], submodule: 'domiciliaciones' },
        { pattern: '/console/domiciliation', modules: ['cobranza'], submodule: 'domiciliaciones' },
        // cobranza.cuotas — Gestión de Cuotas
        { pattern: '/console/installments-console', modules: ['cobranza'], submodule: 'cuotas' },
        // cobranza.notificaciones — Notificaciones por Canal
        { pattern: '/console/notificacion-xlsx', modules: ['cobranza'], submodule: 'notificaciones' },
        // cobranza.banco_plaza — Banco Plaza - Débito Inmediato
        { pattern: '/console/banco-plaza', modules: ['cobranza'], submodule: 'banco_plaza' },
        // cobranza.bdv_consultas — Consultas BDV
        { pattern: '/console/bdv-consultas', modules: ['cobranza'], submodule: 'bdv_consultas' },
        // cobranza.cartera_mora — Cartera y Mora
        { pattern: '/console/cartera', modules: ['cobranza'], submodule: 'cartera_mora' },
        // cobranza.pagos_caja — Pagos en Caja
        { pattern: '/console/caja', modules: ['cobranza'], submodule: 'pagos_caja' },
        // cobranza (dashboard general — requiere acceso a cualquier submódulo de cobranza)
        { pattern: '/console/cobranza', modules: ['cobranza'] },

        // ── Crédito (módulo: credito) ──
        // credito.registro — Registro de Clientes
        { pattern: '/console/client-stadistic', modules: ['credito'], submodule: 'registro' },
        // credito.carga_masiva — Carga Masiva de Clientes
        { pattern: '/console/carga-masiva-cleinte-xlsx', modules: ['credito'], submodule: 'carga_masiva' },
        // credito.aprobacion_masiva — Aprobación Masiva de Clientes
        { pattern: '/console/carga-masiva-aprobaciones-xlsx', modules: ['credito'], submodule: 'aprobacion_masiva' },
        // credito.aumento_cupos_rewards — Aumento Masivo de Cupos con Rewards.
        // ANTES de '/console/carga-masiva-cupos': lo contiene y el match es por url.includes.
        { pattern: '/console/carga-masiva-cupos-rewards', modules: ['credito'], submodule: 'aumento_cupos_rewards' },
        // credito.aumento_cupos — Aumento Masivo de Cupos
        { pattern: '/console/carga-masiva-cupos', modules: ['credito'], submodule: 'aumento_cupos' },
        // credito.inactivacion_cupos — Inactivación Masiva de Cupos
        { pattern: '/console/inactivate-quota', modules: ['credito'], submodule: 'inactivacion_cupos' },
        // credito.historial_cupo — Historial de Cambios de Cupo
        { pattern: '/console/quota-history', modules: ['credito'], submodule: 'historial_cupo' },
        { pattern: '/console/credito/quota-history-global', modules: ['credito'], submodule: 'historial_cupo' },
        // credito.etiquetas — Carga Masiva de Etiquetas
        { pattern: '/console/code-dynamic', modules: ['credito'], submodule: 'etiquetas' },
        // credito.carga_masiva_fees — Carga Masiva de Fees de Reactivación
        { pattern: '/console/credito/reactivaciones-masivas', modules: ['credito'], submodule: 'carga_masiva_fees' },
        // credito (rutas generales del módulo — requieren acceso a cualquier submódulo de crédito)
        { pattern: '/console/approval-policies', modules: ['credito'], submodule: 'politicas_aprobacion' },
        { pattern: '/console/credit-option', modules: ['credito'] },
        { pattern: '/console/credit-calculator', modules: ['credito'] },
        { pattern: '/console/credit-save', modules: ['credito'] },
        { pattern: '/console/credito', modules: ['credito'] },

        // ── Config / Parámetros del Sistema (módulo: config) ──
        // config.usuarios — Gestión de Usuarios
        { pattern: '/console/users', modules: ['config'], submodule: 'usuarios' },
        { pattern: 'register', modules: ['config'], submodule: 'usuarios' },
        // config.roles_permisos — Roles y Permisos
        { pattern: '/console/roles-permisos', modules: ['config'], submodule: 'roles_permisos' },
        // config.seguridad — Seguridad Avanzada
        { pattern: '/console/seguridad-avanzada', modules: ['config'], submodule: 'seguridad' },
        // config.tiendas — Gestión de Tiendas
        { pattern: '/console/shops', modules: ['config'], submodule: 'tiendas' },
        // config.metodos_pago — Métodos de Pago
        { pattern: '/console/payment-options', modules: ['config'], submodule: 'metodos_pago' },
        { pattern: '/console/accounts-admin', modules: ['config'], submodule: 'metodos_pago' },
        // config.productos — Productos
        { pattern: '/console/type_product', modules: ['config'], submodule: 'productos' },
        // config.planes — Planes
        { pattern: '/console/plans', modules: ['config'], submodule: 'planes' },
        // config.etiquetas — Etiquetas
        { pattern: '/console/codes', modules: ['config'], submodule: 'etiquetas' },
        { pattern: '/console/codigos', modules: ['config'], submodule: 'etiquetas' },
        // config.comisiones — Comisiones y Penalidades
        { pattern: '/console/fees', modules: ['config'], submodule: 'comisiones' },
        // config.notificaciones — Notificaciones
        { pattern: '/console/notifications', modules: ['config'], submodule: 'notificaciones' },
        // config.tasas — Tasa de Cambio
        { pattern: '/console/rates', modules: ['config'], submodule: 'tasas' },
        // config.pagos_async — Pagos Asíncronos
        { pattern: '/console/async-admin', modules: ['config'], submodule: 'pagos_async' },
        // config.seguros — Seguros
        { pattern: '/console/parametros-sistema/insurance-console', modules: ['config'], submodule: 'seguros' },
        // config.app_movil — Aplicación Móvil
        { pattern: '/console/parametros-sistema/app-config', modules: ['config'], submodule: 'app_movil' },
        { pattern: '/console/app-config', modules: ['config'], submodule: 'app_movil' },
        // config.procesos_masivos — Procesos Masivos
        { pattern: '/console/procesos-masivos', modules: ['config'], submodule: 'procesos_masivos' },
        // config (dashboard general — requiere acceso a cualquier submódulo de config)
        { pattern: '/console/parametros-sistema', modules: ['config'] },
        { pattern: '/console/recargas-admin', modules: ['config'] },
        { pattern: '/console/recargas', modules: ['config'] },
        { pattern: '/console/online', modules: ['config'] },
        { pattern: '/console/payall-balance', modules: ['finanzas'], submodule: 'verificacion_pagos' },

        // ── Reportes (módulo: reportes) ──
        // reportes.creditos / reportes.seguros
        { pattern: '/console/estadisticas-ejecutivos', modules: ['reportes'], submodule: 'creditos' },
        { pattern: '/console/estadisticas', modules: ['reportes'], submodule: 'creditos' },
        { pattern: '/console/stadistic_approved', modules: ['reportes'], submodule: 'creditos' },
        { pattern: '/console/export', modules: ['reportes'] },
        { pattern: '/console/stadistic', modules: ['reportes'], submodule: 'creditos' },

        // ── Integraciones (módulo: integraciones) ──
        // integraciones.bdv — Banco de Venezuela
        { pattern: '/console/bdv-async-management', modules: ['integraciones'], submodule: 'bdv' },
        { pattern: '/console/bdv-async', modules: ['integraciones'], submodule: 'bdv' },
        // integraciones.domiciliacion_config — Config Domiciliación
        { pattern: '/console/domiciliation-config', modules: ['integraciones'], submodule: 'domiciliacion_config' },

        // ── Productos de Compra (módulo: productos_compra) ──
        // productos_compra.catalogo — Catálogo de Productos
        { pattern: '/console/catalog-products/detail', modules: ['productos_compra'], submodule: 'catalogo' },
        { pattern: '/console/catalog-products', modules: ['productos_compra'], submodule: 'catalogo' },
    ];

    constructor(private router: Router, private _auth: AuthService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        let {profiles} = route?.data;

        // RBAC: si el usuario tiene RBAC, verificar acceso por módulo
        if (this._auth.hasRbac$.getValue()) {
            const url = state.url;
            const match = IsProfileGuard.URL_MODULE_MAP.find(m => url.includes(m.pattern));
            if (match) {
                let hasAccess = false;
                // Si la ruta requiere verificación de submódulo específico
                if (match.submodule) {
                    // Excepción: /console/post (sin :code) se accede también desde
                    // Verificación de Pagos vía botón "Editar" — finanzas.verificacion_pagos.update
                    const allowFromVerificacion = url === '/console/post'
                        && this._auth.hasPermission('finanzas.verificacion_pagos.update');
                    hasAccess = allowFromVerificacion || this._auth.hasSubmoduleAccessOrProfile(
                        match.modules[0],
                        match.submodule,
                        profiles || []
                    );
                } else {
                    // Verificación de módulo general
                    hasAccess = this._auth.hasAnyModuleAccess(match.modules);
                }

                if (!hasAccess) {
                    const defaultRoute = this._auth.getDefaultRoute();
                    console.warn(`[IsProfileGuard] Acceso denegado a ${url} → redirigiendo a ${defaultRoute}`);
                    return this.router.parseUrl(defaultRoute);
                }
                return true;
            }
            // Sin mapeo → permitir (la ruta no está restringida por módulo)
            return true;
        }

        // Legacy: verificar perfiles
        if (!profiles || !profiles.length) return true;
        let sessionUser =  this._auth?.sessionUser?.profile || []; 

        const hasAllElems = sessionUser.some((elem:any) => profiles.includes(elem));

        if (hasAllElems) {
            return true;
        } else {
            console.warn(`[IsProfileGuard] Acceso denegado (legacy) — permaneciendo en vista actual`);
            return false;
        }
    }
}
